import { FC } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { cn } from 'src/utils/cn'
import styles from './keywordComparison.module.scss'
import { KeywordRow } from '../interfaces/comparison.interface'
import { deltaDirection, DeltaDirection } from '../delta'

interface KeywordComparisonProps {
  comparisonLabel: string
  keywords: KeywordRow[]
  primaryLabel: string
}

const COPY = {
  change: 'Change',
  keyword: 'Keyword',
  mentionsSuffix: 'Mentions',
  subtitle: 'Track how keyword mentions change between selected periods.',
  title: 'Keyword Comparison',
  trend: 'Trend',
}

// The sparkline's own coordinate space. Points are normalised into it, so a
// row's numbers can be any scale and still draw the same shape.
// The trend line takes the same three states as the pill beside it.
const SPARK_STROKE: Record<DeltaDirection, string> = {
  down: '#f0663f',
  flat: '#86949d',
  up: '#2e9e4f',
}

const SPARK_WIDTH = 64
const SPARK_HEIGHT = 24

const sparkPoints = (values: number[]) => {
  const min = Math.min(...values)
  const max = Math.max(...values)
  // A flat series would divide by zero; it draws as a line down the middle.
  const span = max - min || 1
  const step = values.length > 1 ? SPARK_WIDTH / (values.length - 1) : 0

  return values
    .map((value, index) => {
      const y = SPARK_HEIGHT - ((value - min) / span) * SPARK_HEIGHT

      return `${index * step},${y}`
    })
    .join(' ')
}

export const KeywordComparison: FC<KeywordComparisonProps> = ({
  comparisonLabel,
  keywords,
  primaryLabel,
}) => (
  <div className={styles.card}>
    <div className={styles.header}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.table}>
      {/* The head sits outside the scrolling body so the columns stay labelled
          however far the list is scrolled. */}
      <div className={cn(styles.row, styles.headRow)}>
        <span className={styles.keywordCell}>{COPY.keyword}</span>
        <span className={styles.numberCell}>
          {primaryLabel} {COPY.mentionsSuffix}
        </span>
        <span className={styles.numberCell}>{comparisonLabel}</span>
        <span className={styles.changeCell}>{COPY.change}</span>
        <span className={styles.trendCell}>{COPY.trend}</span>
      </div>

      <div className={styles.body}>
        {keywords.map((row) => {
          // Same rule as the metric cards: the colour and the arrow follow the
          // number, not whether the move is good news.
          const direction = deltaDirection(row.change)

          return (
            <div className={styles.row} key={row.keyword}>
              <span className={cn(styles.keywordCell, styles.keyword)}>
                {row.keyword}
              </span>
              <span className={cn(styles.numberCell, styles.number)}>
                {row.primaryMentions}
              </span>
              <span className={cn(styles.numberCell, styles.number)}>
                {row.comparisonMentions}
              </span>

              <span className={styles.changeCell}>
                <span
                  className={cn(styles.pill, {
                    [styles.pillGood]: direction === 'up',
                    [styles.pillBad]: direction === 'down',
                    [styles.pillFlat]: direction === 'flat',
                  })}
                >
                  {direction !== 'flat' && (
                    <FontAwesomeIcon
                      className={styles.pillArrow}
                      icon={direction === 'up' ? faArrowUp : faArrowDown}
                    />
                  )}
                  {row.change}
                </span>
              </span>

              <span className={styles.trendCell}>
                <svg
                  className={styles.spark}
                  fill="none"
                  viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
                >
                  <polyline
                    points={sparkPoints(row.trend)}
                    stroke={SPARK_STROKE[direction]}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                </svg>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  </div>
)
